import React from "react";
import { Link } from "gatsby";
import { GridLegacy as Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import logo from "../../../static/img/logo/icon.svg";
import WaysideLogo from "../../../static/img/logo/wayside_logo.svg";
import Section from "../Section";
import Social from "../Social/Social";
import footer from "../../../content/footer/footer.yaml";

const Root = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    textAlign: "center",
  },
  flexGrow: 1,
  padding: theme.spacing(10, 0, 10, 0),
}));

const Logo = styled("img")(({ theme }) => ({
  height: "70px",
  marginBottom: theme.spacing(4),
}));

const Address = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  lineHeight: "180%",
  fontWeight: 500,
  marginBottom: theme.spacing(2),
}));

const SocialSection = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    marginTop: theme.spacing(2),
  },
}));

const Copyright = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  lineHeight: "160%",
  marginTop: theme.spacing(1),
  color: "#212353",
}));

const WaysideContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  "@media (max-width: 600px)": {
    flexDirection: "column",
    textAlign: "center",
  },
});

const WaysideLogoImg = styled("img")(({ theme }) => ({
  height: "50px",
  marginRight: theme.spacing(1),
}));

const WaysideTextContent = styled(Typography)(() => ({
  fontSize: "14px",
  lineHeight: "160%",
  color: "#212353",
  display: "block",
  whiteSpace: "pre-line",
}));

const sxLinksSection = (theme) => ({
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
});

const sxTitle = (theme) => ({
  [theme.breakpoints.up("md")]: {
    fontSize: "21px",
    lineHeight: "180%",
    marginBottom: theme.spacing(6),
  },
  fontSize: "16px",
  lineHeight: "160%",
  fontWeight: 700,
  marginBottom: theme.spacing(3),
});

const sxSocialTitle = (theme) => ({
  [theme.breakpoints.up("md")]: {
    fontSize: "21px",
    lineHeight: "180%",
    marginBottom: theme.spacing(6),
  },
  fontSize: "16px",
  lineHeight: "160%",
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
});

const sxLink = (theme) => ({
  fontSize: "16px",
  lineHeight: "160%",
  marginBottom: theme.spacing(2),
  color: "#212353",
});

const sxEmail = () => ({
  fontSize: "16px",
  lineHeight: "180%",
});

export default function Footer() {
  const { t } = useTranslation();
  return (
    <Root>
      <Section>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={4}>
            <div>
              <Link to="/">
                <Logo src={logo} alt="Nualang" />
              </Link>
            </div>
            <Address>
              {footer.brandName}
              <br />
              {footer.address}
            </Address>
            <Typography
              component="a"
              sx={sxEmail}
              href="mailto:info@nualang.com"
            >
              {footer.email}
            </Typography>
            <Copyright>{footer.copyright}</Copyright>
          </Grid>
          <Grid item xs={6} sm={4} md={2} sx={sxLinksSection}>
            <Typography sx={sxTitle}>{t(footer.about.title)}</Typography>
            {(footer.about.links || []).map((link, i) => (
              <Link to={link.url} key={i}>
                <Typography sx={sxLink}>{t(link.text)}</Typography>
              </Link>
            ))}
          </Grid>
          <Grid item xs={6} sm={4} md={2} sx={sxLinksSection}>
            <Typography sx={sxTitle}>{t(footer.help.title)}</Typography>
            {(footer.help.links || []).map((link, i) => {
              if (link.url.startsWith("https")) {
                return (
                  <a
                    href={link.url}
                    key={i}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <Typography sx={sxLink}>{t(link.text)}</Typography>
                  </a>
                );
              }
              return (
                <Link to={link.url} key={i}>
                  <Typography sx={sxLink}>{t(link.text)}</Typography>
                </Link>
              );
            })}
          </Grid>
          <SocialSection item xs={12} sm={4} md={4}>
            <Typography sx={sxSocialTitle}>{t(footer.social.title)}</Typography>
            <Social disablePadding />
          </SocialSection>
        </Grid>
        <WaysideContainer>
          <WaysideLogoImg src={WaysideLogo} alt="Logo" />
          <div>
            <WaysideTextContent>{footer.waysideTitle}</WaysideTextContent>
            <WaysideTextContent>{footer.waysideAddress}</WaysideTextContent>
          </div>
        </WaysideContainer>
      </Section>
    </Root>
  );
}
